import type {
  ArchiveProspectArgs,
  AssignProspectTicketArgs,
  CreateProspectArgs,
  FetchFormerClientHistoryArgs,
  FetchProspectsArgs,
  FetchProspectTicketsArgs,
  UpdateProspectArgs,
  VoidProspectTicketArgs,
  ApiFormerClientHistory,
  ApiProspectTicket,
  ApiProspect,
} from './types/prospects.types';
import type { BaseApiResponse } from './types/api.types';

import { destroy, patch, post, get } from './axios';

const baseUrl = (organizationId: string) =>
  `/v2/organizations/${organizationId}/prospects`;

export async function fetchProspects({
  organizationId,
  ...query
}: FetchProspectsArgs) {
  const response = await get<{ data: ApiProspect[]; total: number }>(
    baseUrl(organizationId),
    {
      query: {
        limit: query.limit?.toString(),
        offset: query.offset?.toString(),
        orderBy: query.orderBy,
        orderDirection: query.orderDirection,
        search: query.search,
        kind: query.kind,
      },
    },
  );
  return { data: response?.data ?? [], total: response?.total ?? 0 };
}

export async function createProspect({
  organizationId,
  ...data
}: CreateProspectArgs) {
  const response = await post<ApiProspect>(baseUrl(organizationId), data);
  return response?.data ?? null;
}

export async function updateProspect({
  organizationId,
  prospectId,
  ...data
}: UpdateProspectArgs) {
  const response = await patch<ApiProspect>(
    `${baseUrl(organizationId)}/${prospectId}`,
    data,
  );
  return response?.data ?? null;
}

export async function archiveProspect({
  organizationId,
  prospectId,
}: ArchiveProspectArgs) {
  await destroy(`${baseUrl(organizationId)}/${prospectId}`);
}

export async function fetchProspectTickets({
  organizationId,
  prospectId,
}: FetchProspectTicketsArgs) {
  const response = await get<BaseApiResponse<ApiProspectTicket[]>>(
    `${baseUrl(organizationId)}/${prospectId}/tickets`,
  );
  return response?.data ?? [];
}

export async function fetchFormerClientHistory({
  organizationId,
  prospectId,
}: FetchFormerClientHistoryArgs) {
  const response = await get<BaseApiResponse<ApiFormerClientHistory>>(
    `${baseUrl(organizationId)}/${prospectId}/history`,
  );
  return (
    response?.data ?? {
      memberships: [],
      payments: [],
      visits: { count: 0, lastVisitAt: null },
    }
  );
}

export async function assignProspectTicket({
  organizationId,
  prospectId,
  ...data
}: AssignProspectTicketArgs) {
  const response = await post<ApiProspectTicket>(
    `${baseUrl(organizationId)}/${prospectId}/tickets`,
    data,
  );
  return response?.data ?? null;
}

export async function voidProspectTicket({
  organizationId,
  prospectId,
  ticketId,
  reason,
}: VoidProspectTicketArgs) {
  const response = await post<ApiProspectTicket>(
    `${baseUrl(organizationId)}/${prospectId}/tickets/${ticketId}/void`,
    { reason },
  );
  return response?.data ?? null;
}
